import { BE_SUBJECTS_VALUES } from "@/admin/constants/AdminConsoleSubjects/AdminConsoleSubjects";
import {
  FE_USER_ROLES_CONSTANTS,
  ADMIN_CONSOLE_USER_ROLES_CONSTANTS,
} from "@/admin/constants/AdminConsoleUserRolesConstants";
import isValidEmailPattern from "@/utils/isValidEmailPattern";

const validateIfHighestIsHigherThanLowest = ({ lowestGrade, highestGrade }) => {
  /**
   * This function is coded like this, just to make it easier to read
   * on the actual expresion below.
   */
  const [lowestGradeValue, highestGradeValue] = [
    lowestGrade.value.key,
    highestGrade.value.key,
  ];
  return lowestGradeValue <= highestGradeValue;
};

/**
 * Given an object representing the form section values, it returns a boolean flag to indicate if
 * it's active or not.
 *
 * @example true
 * @param {Object} facultyAdderState - An object with the form section values.
 * @returns {Boolean} Boolean flag to tell if it's disabled or not.
 */
export const getButtonStatus = ({ step, userSection, roleSection }) => {
  if (step === 1) {
    return (
      userSection.firstName.value !== "" &&
      userSection.lastName.value !== "" &&
      isValidEmailPattern(userSection.email.value) &&
      userSection.email.isAvailable &&
      userSection.role.value !== ""
    );
  }

  const teacherSectionValid =
    !!roleSection.lowestGrade.value &&
    !!roleSection.highestGrade.value &&
    validateIfHighestIsHigherThanLowest(roleSection) &&
    !!roleSection.school.value &&
    roleSection.subjects.some((grade) => grade.checked);
  if (
    userSection.role.value === FE_USER_ROLES_CONSTANTS.TEACHER ||
    userSection.role.value === FE_USER_ROLES_CONSTANTS.SCHOOL_ADMIN
  ) {
    return teacherSectionValid;
  }
  if (userSection.role.value === FE_USER_ROLES_CONSTANTS.DISTRICT_ADMIN) {
    /**
      If the teacher checkbox is not checked, let the user submit.
      If the teacher checkbox is checked, then check for required inputs.
    **/
    const teacherNotChecked =
      roleSection.isTeacher === null || !roleSection.isTeacher;

    return teacherNotChecked || teacherSectionValid;
  }
};

const getFacultyRoleData = ({ userSection, roleSection }) => {
  const role = userSection.role.value;
  const isTeacher = roleSection.isTeacher;
  const data = { roles: [] };

  const teacherData = {
    lowest_grade: roleSection.lowestGrade.value?.key,
    highest_grade: roleSection.highestGrade.value?.key,
    sis_id: roleSection?.sisId ? roleSection?.sisId : null,
    school_id: roleSection.school.value?.id,
    subjects: roleSection.subjects.reduce((acc, current) => {
      if (current.checked) return [...acc, BE_SUBJECTS_VALUES[current.id]];

      return acc;
    }, []),
  };
  const principalData = {
    school_id: roleSection.school.value?.id,
  };

  switch (role) {
    case FE_USER_ROLES_CONSTANTS.TEACHER:
      data.roles.push(ADMIN_CONSOLE_USER_ROLES_CONSTANTS.TEACHER);
      data.teacher = teacherData;
      break;
    case FE_USER_ROLES_CONSTANTS.SCHOOL_ADMIN:
      data.roles.push(ADMIN_CONSOLE_USER_ROLES_CONSTANTS.SCHOOL_ADMIN);
      data.teacher = teacherData;
      data.principal = principalData;
      break;
    case FE_USER_ROLES_CONSTANTS.DISTRICT_ADMIN:
      data.roles.push(ADMIN_CONSOLE_USER_ROLES_CONSTANTS.DISTRICT_ADMIN);
      if (isTeacher) {
        data.roles.push(ADMIN_CONSOLE_USER_ROLES_CONSTANTS.TEACHER);
        data.teacher = teacherData;
      }
      break;

    default:
      data.roles.push(ADMIN_CONSOLE_USER_ROLES_CONSTANTS.TEACHER);
      data.teacher = teacherData;
      break;
  }

  return data;
};

/**
 * Given an object representing the form section values, it returns the mapped data
 * to make a POST request to the BE.
 * @param {Object} facultyAdderState - An object with the form section values with the form:
 * @example
 *  {
    userSection: {
      firstName: {
        value: '',
        touched: false,
        errorMessage: '',
      },
      lastName: {
        value: '',
        touched: false,
        errorMessage: '',
      },
      email: {
        value: '',
        touched: false,
        errorMessage: '',
      },
      role: {
        value: '',
        touched: false,
        errorMessage: '',
      }
    },
    roleSection: {
      lowestGrade: {
        value: '',
        touched: false,
        errorMessage: '',
      },
      highestGrade: {
        value: '',
        touched: false,
        errorMessage: '',
      },
      sisId: '',
      school: {
        value: '',
        touched: false,
        errorMessage: '',
      },
      isTeacher: null,
      subjects: [
        {
          id: 1,
          name: '',
          checked: false
        }
      ],
    },
    step: 1
  }
  * @returns {Object} Mapped object.
  * @example
  *
  {
    "user": {
        "first_name": "John",
        "last_name": "Doe",
        "email": "test@gmail.com",
    },
    "roles": [
        "principal",
        "district_admin",
        "teacher"
    ],
    "teacher": {
        "lowest_grade": 0,
        "highest_grade": 12,
        "sis_id": 99,
        "school_id": 2000000000,
        "subjects": [
            "math",
            "science",
            "other"
        ]
    },
    "principal": {
        "school_id": 2000000000
    }
  }
*/
export const mapFacultyFormData = (facultyAdderState) => {
  const { userSection } = facultyAdderState;
  const mappedData = {
    user: {
      first_name: userSection.firstName.value,
      last_name: userSection.lastName.value,
      email: userSection.email.value,
    },
    ...getFacultyRoleData(facultyAdderState),
  };

  return mappedData;
};
